import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';

/**
 * Reusable chip cluster with "+N more" / "Show less" toggle.
 *
 * Props:
 *   label         — small uppercase caption above the chips
 *   items         — array of strings OR objects (see itemFormatter)
 *   color         — surface accent (PrimeReact CSS-variable color name)
 *   initialShow   — number of chips visible before the toggle (default 3)
 *   showStar      — render a star on the FIRST chip (used for primary role)
 *   itemFormatter — (item, index) => string (defaults to identity)
 *   emptyHidden   — hide the entire block when items is empty (default true)
 */
const ExpertiseBadgesList = ({
    label,
    items = [],
    color = 'blue',
    initialShow = 3,
    showStar = false,
    itemFormatter = null,
    emptyHidden = true,
}) => {
    const [expanded, setExpanded] = useState(false);

    if (emptyHidden && items.length === 0) return null;

    const visible = expanded ? items : items.slice(0, initialShow);
    const overflow = items.length - initialShow;

    return (
        <div className="mb-3">
            <div className="text-500 text-xs uppercase font-medium mb-2">{label}</div>
            <div className="flex flex-wrap gap-1">
                {visible.map((it, i) => {
                    const display = itemFormatter ? itemFormatter(it, i) : it;
                    const isPrimary = showStar && i === 0;
                    return (
                        <Tag
                            key={`${display}-${i}`}
                            value={isPrimary ? `★ ${display}` : display}
                            className="text-xs"
                            style={{
                                fontSize: '0.65rem',
                                padding: '0.2rem 0.5rem',
                                backgroundColor: `var(--${color}-50)`,
                                color: `var(--${color}-700)`,
                                border: `1px solid var(--${color}-200)`,
                            }}
                        />
                    );
                })}
                {!expanded && overflow > 0 && (
                    <Button
                        label={`+${overflow} more`}
                        text
                        size="small"
                        onClick={() => setExpanded(true)}
                        style={{ padding: '0.15rem 0.5rem', fontSize: '0.7rem' }}
                    />
                )}
                {expanded && items.length > initialShow && (
                    <Button
                        label="Show less"
                        text
                        size="small"
                        onClick={() => setExpanded(false)}
                        style={{ padding: '0.15rem 0.5rem', fontSize: '0.7rem' }}
                    />
                )}
            </div>
        </div>
    );
};

export default ExpertiseBadgesList;
